import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';

import NoReferenceBadge from './NoReferenceBadge';

const CLUSTER_ACCENT_PALETTE = ['primary', 'secondary', 'info', 'success', 'warning'];

const formatCreatedAt = (createdAt) => {
  if (!createdAt) {
    return '';
  }
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleString();
};

const formatRelatedTooltip = (entry) => {
  if (entry.kind === 'singleton') {
    return entry.sampleNames[0] || '';
  }
  return entry.sampleNames.join(', ');
};

const formatRelatedClusterLabel = (label) => (
  label ? `Cluster ${label}` : ''
);

function ClonalityClustersTable({
  samples,
  clusters,
  closelyRelatedBySampleId = new Map(),
  closelySingletonsBySampleId = new Map(),
  noReferenceTableLinksBySampleId = new Map()
}) {
  const clusterIdBySampleId = useMemo(() => {
    const ids = new Map();
    clusters.forEach((cluster) => {
      cluster.sampleIds.forEach((sampleId) => {
        ids.set(sampleId, cluster.id);
      });
    });
    return ids;
  }, [clusters]);

  const labelBySampleId = useMemo(() => {
    const labels = new Map();
    clusters.forEach((cluster) => {
      if (cluster.label == null) {
        return;
      }
      cluster.sampleIds.forEach((sampleId) => {
        labels.set(sampleId, cluster.label);
      });
    });
    return labels;
  }, [clusters]);

  const clusterKindById = useMemo(() => (
    new Map(clusters.map((cluster) => [cluster.id, cluster.kind]))
  ), [clusters]);

  const rowSpanBySampleId = useMemo(() => {
    const spanBySampleId = new Map();
    let index = 0;

    while (index < samples.length) {
      const startSample = samples[index];
      const startClusterId = clusterIdBySampleId.get(startSample.id);
      if (startClusterId == null) {
        spanBySampleId.set(startSample.id, 1);
        index += 1;
        continue;
      }

      let endIndex = index + 1;
      while (endIndex < samples.length && clusterIdBySampleId.get(samples[endIndex].id) === startClusterId) {
        endIndex += 1;
      }

      spanBySampleId.set(startSample.id, endIndex - index);
      index = endIndex;
    }

    return spanBySampleId;
  }, [clusterIdBySampleId, samples]);

  const clusterVisualBySampleId = useMemo(() => {
    const visualBySampleId = new Map();
    let previousClusterKey = null;
    let clusterIndex = -1;

    samples.forEach((sample) => {
      const clusterId = clusterIdBySampleId.get(sample.id);
      const clusterKey = clusterId == null ? `unclustered:${sample.id}` : `cluster:${clusterId}`;
      const isClusterStart = clusterKey !== previousClusterKey;

      if (isClusterStart) {
        clusterIndex += 1;
      }

      const accentRole = clusterId == null || clusterKindById.get(clusterId) !== 'clonal'
        ? null
        : CLUSTER_ACCENT_PALETTE[clusterIndex % CLUSTER_ACCENT_PALETTE.length];

      visualBySampleId.set(sample.id, {
        isStriped: clusterIndex % 2 === 1,
        showClusterDivider: isClusterStart && clusterIndex > 0,
        accentRole
      });

      previousClusterKey = clusterKey;
    });

    return visualBySampleId;
  }, [clusterIdBySampleId, clusterKindById, samples]);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh', overflowX: 'hidden' }}>
      <Table stickyHeader size="small" aria-label="Sample clusters" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          <col style={{ width: '10%' }} />
          <col style={{ width: '23%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '20%' }} />
        </colgroup>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Cluster</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Dataset</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Sample</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Closely related to</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {samples.map((sample) => {
            const relatedEntries = (closelyRelatedBySampleId.get(sample.id) || [])
              .filter((entry) => entry.kind === 'clonal');
            const singletonNeighbors = closelySingletonsBySampleId.get(sample.id) || [];
            const noRefLinks = noReferenceTableLinksBySampleId.get(sample.id) || { segments: [], showChip: false };
            const hasNoRefDisplay = noRefLinks.segments.length > 0;
            const hasAnyRelationships = relatedEntries.length > 0
              || singletonNeighbors.length > 0
              || hasNoRefDisplay;
            const additionalSingletonCount = Math.max(0, singletonNeighbors.length - 1);
            const additionalSingletonLabel = additionalSingletonCount === 1 ? 'sample' : 'samples';
            const clusterVisual = clusterVisualBySampleId.get(sample.id) || {};
            const clusterRowSx = {
              ...(clusterVisual.isStriped ? { backgroundColor: 'grey.50' } : {}),
              ...(clusterVisual.showClusterDivider
                ? { borderTop: (theme) => `2px solid ${theme.palette.divider}` }
                : {})
            };
            const clusterCellAccentSx = {
              ...(clusterVisual.accentRole
                ? {
                  borderLeft: (theme) => `4px solid ${theme.palette[clusterVisual.accentRole].main}`
                }
                : {})
            };

            return (
              <TableRow key={sample.id} hover>
                {rowSpanBySampleId.has(sample.id) && (
                  <TableCell
                    rowSpan={rowSpanBySampleId.get(sample.id)}
                    sx={{ ...clusterRowSx, ...clusterCellAccentSx, verticalAlign: 'middle' }}
                  >
                    {labelBySampleId.has(sample.id)
                      ? <strong>{labelBySampleId.get(sample.id)}</strong>
                      : ''}
                  </TableCell>
                )}
                <TableCell sx={{ ...clusterRowSx, wordBreak: 'break-word' }}>{sample.datasetName || ''}</TableCell>
                <TableCell sx={{ ...clusterRowSx, wordBreak: 'break-word' }}>{sample.name}</TableCell>
                <TableCell sx={{ ...clusterRowSx, wordBreak: 'break-word' }}>
                  {!hasAnyRelationships
                    ? '—'
                    : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <Box component="span">
                          {/* Default closely-related links from SNP distances */}
                          {relatedEntries.map((entry, index) => (
                            <React.Fragment key={`${sample.id}-${entry.clusterId || entry.label}`}>
                              {index > 0 ? ', ' : ''}
                              <Tooltip title={formatRelatedTooltip(entry)}>
                                <Box component="span" sx={{ cursor: 'help' }}>
                                  <strong>{formatRelatedClusterLabel(entry.label)}</strong>
                                </Box>
                              </Tooltip>
                            </React.Fragment>
                          ))}
                          {relatedEntries.length > 0 && singletonNeighbors.length > 0 ? ', ' : ''}
                          {singletonNeighbors.length > 0 && (
                            <Tooltip
                              title={(
                                <Box component="div">
                                  {singletonNeighbors.map((neighbor) => (
                                    <Box key={`${sample.id}-${neighbor.sampleId}`} component="div">
                                      {`${neighbor.sampleName} (${neighbor.distance} SNPs)`}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            >
                              <Box component="span" sx={{ cursor: 'help' }}>
                                <Box component="span" sx={{ fontStyle: 'italic' }}>
                                  {singletonNeighbors[0].sampleName}
                                </Box>
                                {singletonNeighbors.length > 1
                                  ? ` + ${additionalSingletonCount} closely related ${additionalSingletonLabel}`
                                  : ''}
                              </Box>
                            </Tooltip>
                          )}
                          {(relatedEntries.length > 0 || singletonNeighbors.length > 0) && hasNoRefDisplay ? ', ' : ''}
                          {/* No-reference overlay appended after default links */}
                          {noRefLinks.segments.map((segment, index) => (
                            <React.Fragment key={`${sample.id}-no-ref-${segment}`}>
                              {index > 0 ? ', ' : ''}
                              <Box component="span" sx={{ color: 'warning.main' }}>
                                {segment}
                              </Box>
                            </React.Fragment>
                          ))}
                        </Box>
                        {noRefLinks.showChip && <NoReferenceBadge sx={{ flexShrink: 0 }} />}
                      </Box>
                    )}
                </TableCell>
                <TableCell sx={clusterRowSx}>{formatCreatedAt(sample.createdAt)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

ClonalityClustersTable.propTypes = {
  samples: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    datasetName: PropTypes.string,
    createdAt: PropTypes.string
  })).isRequired,
  clusters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    kind: PropTypes.oneOf(['clonal', 'singleton']).isRequired,
    label: PropTypes.string,
    sampleIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    size: PropTypes.number.isRequired
  })).isRequired,
  closelyRelatedBySampleId: PropTypes.instanceOf(Map),
  closelySingletonsBySampleId: PropTypes.instanceOf(Map),
  noReferenceTableLinksBySampleId: PropTypes.instanceOf(Map)
};

export default ClonalityClustersTable;
